import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { marketDataApi } from '../api/marketDataApi';
import { useAuth } from './AuthContext';

const EarningsBookContext = createContext(null);

function datePlusDays(base, days) {
  const d = new Date(`${base}T12:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export const EarningsBookProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [bySymbol, setBySymbol] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setBySymbol({});
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const today = todayIso();
        const tomorrow = datePlusDays(today, 1);
        const [todayRes, tomorrowRes] = await Promise.all([
          marketDataApi.yahoo.getEarningsCalendar({ day: today, size: 200 }),
          marketDataApi.yahoo.getEarningsCalendar({ day: tomorrow, size: 200 }),
        ]);

        if (cancelled) return;

        const map = {};
        (todayRes.data || []).forEach((row) => {
          if (!row.symbol) return;
          map[row.symbol] = {
            when: 'today',
            timing: row.timing,
            company: row.company,
            date: row.date || today,
          };
        });
        (tomorrowRes.data || []).forEach((row) => {
          if (!row.symbol || map[row.symbol]?.when === 'today') return;
          map[row.symbol] = {
            when: 'tomorrow',
            timing: row.timing,
            company: row.company,
            date: row.date || tomorrow,
          };
        });
        setBySymbol(map);
      } catch (err) {
        console.error('Failed to load earnings book map:', err);
        if (!cancelled) setBySymbol({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const interval = setInterval(load, 30 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  const getEarningsTag = useCallback(
    (symbol) => {
      if (!symbol) return null;
      return bySymbol[String(symbol).toUpperCase()] || null;
    },
    [bySymbol]
  );

  const value = useMemo(
    () => ({ bySymbol, loading, getEarningsTag }),
    [bySymbol, loading, getEarningsTag]
  );

  return (
    <EarningsBookContext.Provider value={value}>{children}</EarningsBookContext.Provider>
  );
};

export const useEarningsBook = () => {
  const ctx = useContext(EarningsBookContext);
  if (!ctx) throw new Error('useEarningsBook must be used within EarningsBookProvider');
  return ctx;
};

export const EarningsBadge = ({ symbol, className = '' }) => {
  const { getEarningsTag } = useEarningsBook();
  const tag = getEarningsTag(symbol);
  if (!tag) return null;
  const label = tag.when === 'today' ? 'Earn today' : 'Earn tmrw';
  const tone =
    tag.when === 'today'
      ? 'bg-[rgba(232,184,74,0.16)] text-[var(--lt-warning)] border-[rgba(232,184,74,0.35)]'
      : 'bg-[var(--lt-action-dim)] text-action border-hairline';
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider border ${tone} ${className}`}
      title={`${label}${tag.timing ? ` · ${tag.timing}` : ''}`}
    >
      {label}
    </span>
  );
};
